/**
 * Como a pessoa entra: pela primeira vez, no convite, ou de novo, depois de o
 * administrador redefinir a senha dela (#1167).
 *
 * ── Por que isto é uma tela, e não um recado ──────────────────────────────
 *
 * Porque num dos desfechos há uma senha que **aparece uma única vez**. Um
 * recado que some sozinho depois de alguns segundos seria o pior lugar possível
 * para ela: quem convidou olha para o lado, o recado vai embora, e a pessoa
 * fica sem acesso — com a conta já criada e ninguém sabendo a senha.
 *
 * Nos outros desfechos não há segredo nenhum, e um recado bastaria. Mas ter
 * dois lugares para a mesma resposta obrigaria quem lê o código a descobrir
 * qual deles está em uso hoje.
 *
 * ── Uma janela para os dois momentos ──────────────────────────────────────
 *
 * A senha do convite e a da redefinição são a mesma coisa para quem copia: uma
 * senha provisória, mostrada uma vez, que o login obriga a trocar. Muda só o
 * que se diz em volta dela, e é o `momento` que decide.
 */

import { useState } from 'react';
import type { PrimeiroAcesso } from '@cliqcrm/contracts';
import { Botao, Dialogo } from '@/shared/ui';
import './primeiro-acesso.css';

export function PrimeiroAcessoDaPessoa({
  nome,
  acesso,
  aoFechar,
  momento = 'convite',
  sessoesEncerradas = true,
}: {
  nome: string;
  acesso: PrimeiroAcesso;
  aoFechar: () => void;
  /** `redefinicao`: a senha foi redefinida por quem administra, e a antiga já não vale. */
  momento?: 'convite' | 'redefinicao';
  /** Só na redefinição: `false` quando o login não confirmou que encerrou as sessões abertas. */
  sessoesEncerradas?: boolean;
}) {
  const [copiado, definirCopiado] = useState(false);
  const redefinicao = momento === 'redefinicao';
  const acessoDaVez = redefinicao ? 'próximo acesso' : 'primeiro acesso';

  const copiar = () => {
    if (acesso.via !== 'senha_provisoria') return;
    void navigator.clipboard?.writeText(acesso.senha).then(() => definirCopiado(true));
  };

  return (
    <Dialogo
      titulo={redefinicao ? `Senha de ${nome} redefinida` : `${nome} entrou`}
      aoFechar={aoFechar}
      botoes={
        <Botao variante="primario" onClick={aoFechar}>
          Entendi
        </Botao>
      }
    >
      {acesso.via === 'email' && (
        <p className="primeiro-acesso__texto">
          Enviamos para <b>{acesso.enviadoPara}</b> um link para {nome}{' '}
          {redefinicao ? 'definir uma senha nova' : 'definir a própria senha'}. Ele vale três dias.
          {redefinicao && ' A senha antiga já não funciona.'}
        </p>
      )}

      {acesso.via === 'ja_tinha_conta' && (
        /*
         * Não é um caso de erro nem de pendência: a mesma pessoa pode atender
         * duas revendas com um login só. Dizer isso evita que alguém saia
         * procurando uma senha que não existe — ou, pior, redefina a senha
         * dela e quebre o acesso na outra revenda.
         */
        <p className="primeiro-acesso__texto">
          {nome} já tinha conta no CliqCRM e entra com a senha que já usa. Não há senha nova para
          enviar.
        </p>
      )}

      {acesso.via === 'senha_provisoria' && (
        <>
          <p className="primeiro-acesso__texto">
            {redefinicao
              ? `A senha antiga já não funciona. Passe esta a ${nome} pelo canal que vocês usam: ela vale para o próximo acesso.`
              : `Passe esta senha a ${nome} pelo canal que vocês usam. Ela vale para o primeiro acesso.`}
          </p>

          {/*
            O cartão da senha: o que ela é em cima, a senha no meio, grande, e o
            copiar AO LADO dela — a ação que esta janela existe para pedir. O
            botão é o primário até copiar; depois vira a confirmação, em verde,
            para quem olhar de novo saber que já foi.
          */}
          <div className="primeiro-acesso__cartao">
            <div className="primeiro-acesso__topo">
              <span className="primeiro-acesso__chave" aria-hidden="true">
                <svg
                  width={18}
                  height={18}
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.7}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="7" cy="12.6" r="3.6" />
                  <path d="m9.6 10 6.6-6.6M14 5.6l2 2M12 7.6l1.6 1.6" />
                </svg>
              </span>
              <span className="primeiro-acesso__rotulo">Senha provisória</span>
              <span className="primeiro-acesso__validade">Só para o {acessoDaVez}</span>
            </div>

            <div className="primeiro-acesso__caixa">
              <span className="primeiro-acesso__senha">{acesso.senha}</span>
              <Botao variante={copiado ? 'secundario' : 'primario'} onClick={copiar}>
                {copiado ? 'Copiada' : 'Copiar senha'}
              </Botao>
            </div>
            <span className="primeiro-acesso__copiada" role="status">
              {copiado ? 'Senha copiada para a área de transferência.' : ''}
            </span>
          </div>

          <p className="primeiro-acesso__aviso">
            <span className="primeiro-acesso__aviso-icone" aria-hidden="true">
              !
            </span>
            <span>
              Ela aparece uma vez só — fechando esta janela, não dá para vê-la de novo. {nome} vai
              ser obrigada a trocá-la no {acessoDaVez}.
            </span>
          </p>
        </>
      )}

      {redefinicao && !sessoesEncerradas && (
        /*
         * A senha foi trocada, e isso não muda. O que o login não confirmou
         * foi tirar quem já estava dentro: dizer que todos saíram seria
         * mentir sobre a parte que importa quando a senha vazou.
         */
        <p className="primeiro-acesso__aviso">
          <span className="primeiro-acesso__aviso-icone" aria-hidden="true">
            !
          </span>
          <span>
            O login não confirmou o encerramento das sessões abertas: quem já estava conectado com a
            senha antiga continua até sair.
          </span>
        </p>
      )}
    </Dialogo>
  );
}
